<script setup>
import { ref, watch, computed } from "vue";
import { Head, useForm } from "@inertiajs/vue3";
import Select from "./Components/Form/Select.vue";
import SaveButton from "./Components/Form/SaveButton.vue";
import { router } from "@inertiajs/vue3";
import CustomLink from "./Components/CustomLink.vue";
import Subfooter from "./Includes/Subfooter.vue";
import Checkbox from "./Components/Form/Checkbox.vue";

const eurFormatter = new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0
});

const props = defineProps({
    vehicles: Array,
});

const minYear = Math.min(...props.vehicles.map(vehicle => vehicle.year));
const maxYear = Math.max(...props.vehicles.map(vehicle => vehicle.year));
const constructionYears = ref(
    Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i).map(year => ({ id: year, label: year }))
);

const filteredConstructionYears = computed(() => {
    const fromYear = searchForm.construction_year[0];
    return constructionYears.value.filter(year => year.id >= fromYear);
});

const mileages = ref(
    Array.from({ length: 400000 / 10000 }, (_, i) => i * 10000).map(mileage => ({ id: mileage, label: mileage.toLocaleString() + ' km' }))
);

const filteredMileages = computed(() => {
    const fromMileage = searchForm.mileage[0];
    return mileages.value.filter(mileage => mileage.id >= fromMileage);
});

const prices = ref(
    Array.from({ length: 150000 / 10000 }, (_, i) => i * 10000).map(price => ({ id: price, label: eurFormatter.format(price) }))
);

const searchForm = useForm({
    brand: null,
    model: null,
    type: null,
    price: [null, null],
    construction_year: [null, null],
    mileage: [null, null],
    fuel: null,
    transmission: null,
    body: null
})

//watch all form values
const brandOptions = ref([...new Set(props.vehicles.map(vehicle => vehicle.brand))].map(brand => ({ id: brand, label: brand })));

const modelOptions = ref([]);
watch(
    () => searchForm.brand,
    (newBrand) => {
        if (newBrand) {
            modelOptions.value = [...new Set(
                props.vehicles
                    .filter(vehicle => vehicle.brand === newBrand)
                    .map(vehicle => vehicle.model)
            )].map(model => ({ id: model, label: model }));
        } else {
            modelOptions.value = [{ id: null, label: 'Geen resultaten gevonden' }];
            typeOptions.value = [{ id: null, label: 'Geen resultaten gevonden' }];
        }

        //when brand has changed, model should be reset
        searchForm.model = null;
        searchForm.type = null;
    }
);

const typeOptions = ref([]);
watch(
    () => searchForm.model,
    (newModel) => {
        if (newModel) {
            typeOptions.value = [...new Set(
                props.vehicles
                    .filter(vehicle => vehicle.brand === searchForm.brand)
                    .filter(vehicle => vehicle.model === newModel)
                    .map(vehicle => vehicle.type)
            )].map(type => ({ id: type, label: type }));
        } else {
            typeOptions.value = [{ id: null, label: 'Geen resultaten gevonden' }];
        }

        //when model has changed, type should be reset
        searchForm.type = null;
    }
);

const bodyTypes = ref([...new Set(props.vehicles.map(vehicle => vehicle.body))].map(body => ({ id: body, label: body })));
const fuelTypes = ref([...new Set(props.vehicles.map(vehicle => vehicle.fuel))].map(fuel => ({ id: fuel, label: fuel })));
const transmissionTypes = ref([...new Set(props.vehicles.map(vehicle => vehicle.transmission))].map(transmission => ({ id: transmission, label: transmission })));

function submit() {
    const filterParams = {
        brand: searchForm.brand,
        model: searchForm.model,
        type: searchForm.type,
        body: searchForm.body,
        fuel: searchForm.fuel,
        transmission: searchForm.transmission,
        price: searchForm.price,
        construction_year: searchForm.construction_year,
        mileage: searchForm.mileage,
        accessories: selectedAccessoires.value,
        exterior_color: selectedExteriorColor.value,
        interior_color: selectedInteriorColor.value,
        upholstery: selectedInteriorMaterial.value
    };
    localStorage.setItem('filterParams', JSON.stringify(filterParams));
    router.visit('/aanbod');
}

const accessoiresOptions = ref([...new Set(props.vehicles.map(vehicle => vehicle.accessories).flat().filter(Boolean))].map(accessoire => ({ name: accessoire })));
const exteriorColorOptions = ref([...new Set(props.vehicles.map(vehicle => vehicle.color).filter(Boolean))].map(color => ({ name: color.charAt(0).toUpperCase() + color.slice(1) })));
const interiorColorsOptions = ref([...new Set(props.vehicles.map(vehicle => vehicle.interior_color).filter(Boolean))].map(color => ({ name: color })));
const interiorMaterialsOptions = ref([...new Set(props.vehicles.map(vehicle => vehicle.upholstery).filter(Boolean))].map(material => ({ name: material })));

const selectedAccessoires = ref([]);
const selectedExteriorColor = ref([]);
const selectedInteriorColor = ref([]);
const selectedInteriorMaterial = ref([]);
</script>

<template>

    <Head>
        <title>Uitgebreid Zoeken naar Gebruikte Auto's bij Zwagers Automotive</title>
    </Head>

    <div class="py-20">
        <form @submit.prevent="submit">
            <div class="w-11/12 px:0 md:px-6 xl:px-0 max-w-6xl mx-auto p-5 pb-8 rounded-lg">
                <div class="flex flex-col items-start gap-16 w-full pb-12">
                    <div class="flex flex-col items-start gap-6 lg:pt-16">
                        <p>Uitgebreid zoeken</p>
                        <h2>Vind jouw auto</h2>
                        <p>Bekijk ons brede aanbod aan hoogwaardige voertuigen om jouw perfecte match te vinden.</p>
                    </div>
                    <div class="mt-4 w-full grid grid-cols-1 sm:grid-cols-3 text-sm font-normal text-gray-900 gap-3 md:gap-5">
                        <div class="col-span-full sm:col-span-1">
                            <Select v-model="searchForm.brand" :options="brandOptions" label="Merk" valueName="label" clear valueId="id" placeholder="Selecteer een merk..." />
                        </div>
                        <div class="col-span-full sm:col-span-1">
                            <Select v-model="searchForm.model" :options="modelOptions" label="Model" valueName="label" clear valueId="id" placeholder="Selecteer een model..." />
                        </div>
                        <div class="col-span-full sm:col-span-1">
                            <Select v-model="searchForm.type" :options="typeOptions" label="Variant" valueName="label" clear valueId="id" placeholder="Selecteer een type..." />
                        </div>
                        <div class="col-span-full sm:col-span-1">
                            <Select v-model="searchForm.body" :options="bodyTypes" label="Carrosserie" valueName="label" clear valueId="id" placeholder="Selecteer een carrosserie..." />
                        </div>
                        <div class="col-span-full sm:col-span-1">
                            <Select v-model="searchForm.fuel" :options="fuelTypes" label="Brandstof" valueName="label" clear valueId="id" placeholder="Selecteer een brandstof..." />
                        </div>
                        <div class="col-span-full sm:col-span-1">
                            <Select v-model="searchForm.transmission" :options="transmissionTypes" label="Transmissie" valueName="label" clear valueId="id" placeholder="Selecteer een transmissie..." />
                        </div>
                        <div class="col-span-full sm:col-span-1 grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-2 lg:gap-4">
                            <div>
                                <Select :options="prices" valueName="label" valueId="id" label="Prijs" v-model="searchForm.price[0]" placeholder="Van" />
                            </div>
                            <div class="content-end">
                                <Select :options="prices" valueName="label" valueId="id" label="" v-model="searchForm.price[1]" placeholder="Tot" />
                            </div>
                        </div>
                        <div class="col-span-full sm:col-span-1 grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-2 lg:gap-4">
                            <div>
                                <Select v-model="searchForm.construction_year[0]" :options="constructionYears" label="Bouwjaar" valueName="label" valueId="id" placeholder="Van" />
                            </div>
                            <div class="content-end">
                                <Select v-model="searchForm.construction_year[1]" :options="filteredConstructionYears" label="" valueName="label" valueId="id" placeholder="Tot" />
                            </div>
                        </div>
                        <div class="col-span-full sm:col-span-1 grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-2 lg:gap-4">
                            <div>
                                <Select v-model="searchForm.mileage[0]" :options="mileages" label="Kilometerstand" valueName="label" valueId="id" placeholder="Van" />
                            </div>
                            <div class="content-end">
                                <Select v-model="searchForm.mileage[1]" :options="filteredMileages" label="" valueName="label" valueId="id" placeholder="Tot" />
                            </div>
                        </div>
                    </div>
                </div>
                <p class="font-bold pb-4">Accessoires</p>
                <div class="grid grid-cols-1 gap-x-5 gap-y-0 sm:grid-cols-2 lg:grid-cols-3 pb-12">
                    <div v-for="(accessoiresOption, index) in accessoiresOptions" :key="index"
                        class="flex align-items-center">
                        <Checkbox v-model="selectedAccessoires" name="category" :value="accessoiresOption.name" :label="accessoiresOption.name"
                            :inputId="'acc_'+accessoiresOption.name" />
                    </div>
                </div>
                <p class="font-bold pb-4">Exterieur kleuren</p>
                <div class="grid grid-cols-1 gap-x-5 gap-y-0 sm:grid-cols-3 lg:grid-cols-5 pb-12">
                    <div v-for="(exteriorColorOption, index) in exteriorColorOptions" :key="index"
                        class="flex align-items-center">
                        <Checkbox v-model="selectedExteriorColor" name="category" :value="exteriorColorOption.name"
                            :label="exteriorColorOption.name" :inputId="'ext_color_' + exteriorColorOption.name" />
                    </div>
                </div>
                <p class="font-bold pb-4">Interieur kleur</p>
                <div class="grid grid-cols-1 gap-x-5 gap-y-0 sm:grid-cols-3 lg:grid-cols-5 pb-12">
                    <div v-for="(interiorColorOption, index) in interiorColorsOptions" :key="index"
                        class="flex align-items-center">
                        <Checkbox v-model="selectedInteriorColor" name="category" :value="interiorColorOption.name"
                        :label="interiorColorOption.name" :inputId="'int_color_' + interiorColorOption.name" />
                    </div>
                </div>
                <p class="font-bold pb-4">Interieur kleur</p>
                <div class="grid grid-cols-1 gap-x-5 gap-y-0 sm:grid-cols-3 lg:grid-cols-5 pb-12">
                    <div v-for="(interiorMaterialOption, index) in interiorMaterialsOptions" :key="index"
                        class="flex align-items-center">
                        <Checkbox v-model="selectedInteriorMaterial" name="category"
                            :value="interiorMaterialOption.name" 
                            :label="interiorMaterialOption.name" :inputId="'upholstery_' + interiorMaterialOption.name" />
                    </div>
                </div>
                <div class="mt-5">
                    <SaveButton text="Resultaten tonen" class="primary-button mx-auto" />
                </div>
            </div>
        </form>
    </div>
</template>
